/**
 * ResourcesTab Component
 *
 * Second tab of the Investment Editor with 3 sections for different resource types:
 * 1. Internal Labor Resources (resource_type = 'I')
 * 2. External Labor Resources (resource_type = 'E')
 * 3. Other Resources - Systems, Licenses, Tools (resource_type = 'O')
 */

import {
  useResources,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  resourceKeys,
} from '@medsuite/api-client';
import { Input, Button, Card, DatePicker } from '@medsuite/ui-components';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import type { CreateResourceDto, UpdateResourceDto } from '@medsuite/api-client';
import type { Investment, InvestmentResource, ResourceType } from '@medsuite/models';

interface ResourcesTabProps {
  investment: Investment;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  onRegisterSaveFunctions?: (funcs: Record<string, () => Promise<void>>) => void;
}

/**
 * Helper function to convert date to yyyy-MM-dd format for date inputs
 */
const formatDateForInput = (date: Date | string | null): string => {
  if (!date) {
    return '';
  }
  const d = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(d.getTime())) {
    return '';
  }
  return d.toISOString().split('T')[0] ?? '';
};

/**
 * ResourcesTab Component
 */
export const ResourcesTab: React.FC<ResourcesTabProps> = ({
  investment,
  isEditing: _isEditing,
  setIsEditing,
  onRegisterSaveFunctions,
}) => {
  const investmentId = investment.investment_id;
  const queryClient = useQueryClient();

  // Fetch all resources for this investment
  const { data: resources = [], isLoading } = useResources(investmentId);

  // Mutations
  const createResource = useCreateResource(investmentId);
  const updateResource = useUpdateResource(investmentId);
  const deleteResource = useDeleteResource(investmentId);

  // Use ref to store resources (survives remounts and prevents stale closures)
  const resourcesRef = useRef<InvestmentResource[]>([]);

  // Local state for editable resources
  const [localResources, setLocalResources] = useState<InvestmentResource[]>([]);

  // Track previous investment ID to detect changes
  const prevInvestmentIdRef = useRef<number | null>(null);
  const prevResourcesRef = useRef<string>('');

  // Initialize local state when data loads or investment changes
  useEffect(() => {
    const resourcesJson = JSON.stringify(resources);

    // Only update if investment changed or resources data changed
    if (
      prevInvestmentIdRef.current !== investmentId ||
      prevResourcesRef.current !== resourcesJson
    ) {
      setLocalResources(resources);
      resourcesRef.current = resources; // Also update ref
      prevInvestmentIdRef.current = investmentId;
      prevResourcesRef.current = resourcesJson;
    }
  }, [investmentId, resources]);

  // Keep ref in sync with local state changes
  useEffect(() => {
    resourcesRef.current = localResources;
  }, [localResources]);

  // Filter resources by type
  const internalResources = localResources.filter((r) => r.resource_type === 'I');
  const externalResources = localResources.filter((r) => r.resource_type === 'E');
  const otherResources = localResources.filter((r) => r.resource_type === 'O');

  // Add resource handler
  const handleAddResource = (resourceType: ResourceType) => {
    const baseResource: InvestmentResource = {
      investment_resource_id: 0,
      investment_id: investmentId,
      resource_type: resourceType,
      resource_name: '',
      project_role: null,
      resource_start_date: null,
      resource_end_date: null,
      resource_allocation: null,
      quantifier: null,
      temporary_or_perpetual: null,
    };

    setLocalResources([...localResources, baseResource]);
    setIsEditing(true);
  };

  // Update resource handler
  const handleUpdateResource = (index: number, updates: Partial<InvestmentResource>) => {
    const updated = [...localResources];
    const existing = updated[index];
    if (!existing) {
      return;
    }
    updated[index] = { ...existing, ...updates };
    setLocalResources(updated);
    setIsEditing(true);
  };

  // Delete resource handler
  const handleDeleteResource = async (index: number) => {
    const resource = localResources[index];
    if (!resource) {
      return;
    }

    if (resource.investment_resource_id > 0) {
      // If it's persisted, delete from server
      try {
        await deleteResource.mutateAsync(resource.investment_resource_id);
      } catch (error) {
        console.error('Failed to delete resource:', error);
        return;
      }
    }

    // Remove from local state
    setLocalResources(localResources.filter((_, i) => i !== index));
    setIsEditing(true);
  };

  // Save all changes - use ref to avoid stale closures
  const handleSaveAll = useCallback(async () => {
    try {
      for (const resource of resourcesRef.current) {
        // Convert null to undefined for DTO compatibility
        const resourceDto = {
          ...resource,
          project_role: resource.project_role ?? undefined,
        };
        if (resource.investment_resource_id === 0) {
          // Create new resource
          await createResource.mutateAsync(resourceDto as unknown as Omit<CreateResourceDto, 'investment_id'>);
        } else {
          // Update existing resource
          await updateResource.mutateAsync({
            resourceId: resource.investment_resource_id,
            data: resourceDto as unknown as UpdateResourceDto,
          });
        }
      }

      // Explicitly refetch the resources to ensure we have fresh data
      // This is non-critical - if it fails, the save still succeeded
      try {
        await queryClient.refetchQueries({
          queryKey: resourceKeys.list(investmentId),
          exact: true,
        });
      } catch (refetchError) {
        console.warn('Resource refetch failed (non-critical):', refetchError);
      }
    } catch (error) {
      console.error('Failed to save resources:', error);
      throw error; // Re-throw so parent can handle
    }
  }, [createResource, updateResource, queryClient, investmentId]); // Only mutation dependencies, NOT localResources

  // Register save function with parent component (only once)
  useEffect(() => {
    if (onRegisterSaveFunctions) {
      onRegisterSaveFunctions({
        saveResources: handleSaveAll,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onRegisterSaveFunctions]); // Only re-register if callback changes, not when handleSaveAll changes

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Loading resources...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Section 1: Internal Labor Resources */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                1. Internal Labor Resources
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Internal employees allocated to this investment
              </p>
            </div>
            <Button onClick={() => handleAddResource('I')} size="sm">
              + Add Internal Resource
            </Button>
          </div>

          {internalResources.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No internal resources yet. Click &quot;Add Internal Resource&quot; to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Resource Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Project Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Allocation %
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {localResources.map((resource, index) => {
                    if (resource.resource_type !== 'I') {
                      return null;
                    }
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.resource_name}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_name: e.target.value,
                              })
                            }
                            placeholder="Name"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.project_role || ''}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                project_role: e.target.value,
                              })
                            }
                            placeholder="Role"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <DatePicker
                            value={formatDateForInput(resource.resource_start_date)}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_start_date: e.target.value
                                  ? new Date(e.target.value)
                                  : null,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4">
                          <DatePicker
                            value={formatDateForInput(resource.resource_end_date)}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_end_date: e.target.value ? new Date(e.target.value) : null,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            type="number"
                            value={resource.resource_allocation || ''}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_allocation: parseFloat(e.target.value) || null,
                              })
                            }
                            placeholder="%"
                            min="0"
                            max="100"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Button
                            onClick={() => handleDeleteResource(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Section 2: External Labor Resources */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                2. External Labor Resources
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Contractors and external staff allocated to this investment
              </p>
            </div>
            <Button onClick={() => handleAddResource('E')} size="sm">
              + Add External Resource
            </Button>
          </div>

          {externalResources.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No external resources yet. Click &quot;Add External Resource&quot; to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Resource Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Project Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Start Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Allocation %
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {localResources.map((resource, index) => {
                    if (resource.resource_type !== 'E') {
                      return null;
                    }
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.resource_name}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_name: e.target.value,
                              })
                            }
                            placeholder="Name"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.project_role || ''}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                project_role: e.target.value,
                              })
                            }
                            placeholder="Role"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <DatePicker
                            value={formatDateForInput(resource.resource_start_date)}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_start_date: e.target.value
                                  ? new Date(e.target.value)
                                  : null,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4">
                          <DatePicker
                            value={formatDateForInput(resource.resource_end_date)}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_end_date: e.target.value ? new Date(e.target.value) : null,
                              })
                            }
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            type="number"
                            value={resource.resource_allocation || ''}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_allocation: parseFloat(e.target.value) || null,
                              })
                            }
                            placeholder="%"
                            min="0"
                            max="100"
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Button
                            onClick={() => handleDeleteResource(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Section 3: Other Resources */}
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                3. Other Resources (Systems, Licenses, Tools)
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Hardware, software, licenses, and other non-labor resources
              </p>
            </div>
            <Button onClick={() => handleAddResource('O')} size="sm">
              + Add Other Resource
            </Button>
          </div>

          {otherResources.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No other resources yet. Click &quot;Add Other Resource&quot; to get started.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Resource Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Quantifier
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Temporary or Perpetual
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {localResources.map((resource, index) => {
                    if (resource.resource_type !== 'O') {
                      return null;
                    }
                    return (
                      <tr key={index}>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.resource_name}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                resource_name: e.target.value,
                              })
                            }
                            placeholder="Resource name"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <Input
                            value={resource.quantifier || ''}
                            onChange={(e) =>
                              handleUpdateResource(index, {
                                quantifier: e.target.value,
                              })
                            }
                            placeholder="e.g., '10 licenses', '1 server'"
                          />
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-4">
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`temp_perp_${index}`}
                                value="T"
                                checked={resource.temporary_or_perpetual === 'T'}
                                onChange={(e) =>
                                  handleUpdateResource(index, {
                                    temporary_or_perpetual: e.target.value,
                                  })
                                }
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">Temporary</span>
                            </label>
                            <label className="flex items-center">
                              <input
                                type="radio"
                                name={`temp_perp_${index}`}
                                value="P"
                                checked={resource.temporary_or_perpetual === 'P'}
                                onChange={(e) =>
                                  handleUpdateResource(index, {
                                    temporary_or_perpetual: e.target.value,
                                  })
                                }
                                className="mr-2"
                              />
                              <span className="text-sm text-gray-700">Perpetual</span>
                            </label>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          <Button
                            onClick={() => handleDeleteResource(index)}
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default ResourcesTab;
